import { getDecimalsForFormattedCryptoPrecision } from './getDecimalsForFormattedCryptoPrecision';

enum Notation {
    Standard = 'standard',
    Compact = 'compact'
}
export class NumberFormatter {
    constructor(
        public language: string,
        public formatOptions: {
            notation?: Notation;
            maximumSignificantDigits?: number;
            maximumFractionDigits?: number;
            minimumFractionDigits?: number;
            currency?: string;
            style?: 'currency';
            useGrouping?: boolean;
        } = {
            notation: Notation.Standard,
        }
    ) {}
  
  isCompactNumber(): void {
    this.formatOptions.notation = Notation.Compact;
  }
  
  setMaxSignificantDigits(maxDigits: number): void {
    this.formatOptions.maximumSignificantDigits = maxDigits;
  }

  setMaxFractionDigits(maxDigits: number): void {
    this.formatOptions.maximumFractionDigits = maxDigits;
  }

  setMinimumFractionDigits(minDigits: number): void {
    this.formatOptions.minimumFractionDigits = minDigits;
  }
  
  setCurrency(currency: string): void {
    this.formatOptions.currency = currency;
    this.formatOptions.style = 'currency';
  }

  setUseGrouping(val: boolean): void {
    this.formatOptions.useGrouping = val;
  }

  format(number: number): string {
    const locale = `${this.language}-${this.language.toUpperCase()}`;
    const formatter = new Intl.NumberFormat(locale, this.formatOptions);
    
    return formatter.format(number);
  }

  // 0.00000001234 = >  0.00000001
  getAmountAndDecimalsForExtraSmallAmount(number: number, decimals: number): [number, number] {
    let [integerPart, fractionPart] = parseFloat(number as unknown as string).toFixed(20).split('.');
    fractionPart = (fractionPart || '').replace(/0+$/, '');

    if (fractionPart && number > 0 && number < 0.01 && fractionPart.length > decimals) {
      const match = fractionPart.match(/[0]{1,}[^0]/g);
      if (match && match[0]) {
        const [minimumPossibleFraction] = match;
        decimals = minimumPossibleFraction.length;
        if (decimals > 10) decimals = 10;
        fractionPart = fractionPart.substring(0, decimals);
        number = Number(`${integerPart}.${fractionPart}`);
      }
    }
    return [number, decimals];
  }
}

function getStandardNumberFormatter(_number: number, language: string, currency: string | null): NumberFormatter {
  const numberFormatter = new NumberFormatter(language);
  if (currency !== null) {
    numberFormatter.setCurrency(currency);
  }
  
  return numberFormatter;
}

function getCompactNumberFormatter(_number: number, language: string, currency: string | null): NumberFormatter {
  const numberFormatter = getStandardNumberFormatter(_number, language, currency);
  numberFormatter.isCompactNumber();
  
  return numberFormatter;
}

export function formatNumberCompact(number: number, language: string, currency: string | null = null): string {
  const numberFormatter = getCompactNumberFormatter(number, language, currency);
  
  return numberFormatter.format(number);
}

export function formatNumberStandard(number: number, language: string, currency: string | null = null, minimumFractionDigits: number | null = null): string {
  const numberFormatter = getStandardNumberFormatter(number, language, currency);

  if (number > 0 && number < 0.01) {
    let decimals = 2;
    [number, decimals] = numberFormatter.getAmountAndDecimalsForExtraSmallAmount(number, decimals);
    numberFormatter.setMaxFractionDigits(decimals);
  }

  if (minimumFractionDigits !== null) {
    numberFormatter.setMinimumFractionDigits(minimumFractionDigits);
  }
  
  return numberFormatter.format(number);
}

export function formatNumberForCrypto(number: number, language: string, cryptoCurrency: string): string {
  const decimals = getDecimalsForFormattedCryptoPrecision(cryptoCurrency);

  const numberFormatter = new NumberFormatter(language);
  numberFormatter.setMaxFractionDigits(decimals);

  return numberFormatter.format(number);
}

// Transform one number to another number format
// Example: 112345.120000 => 112345.12 , 0.00000162 => 0.000001
export function formatNumberWithDecimals(amount: number, maximumFractionDigits: number, transformExtraSmallAmount: boolean = false): string {
  const numberFormatter = new NumberFormatter('en');
  if(transformExtraSmallAmount) {
    [amount, maximumFractionDigits] = numberFormatter.getAmountAndDecimalsForExtraSmallAmount(amount, maximumFractionDigits);
  }
  numberFormatter.setUseGrouping(false);
  numberFormatter.setMaxFractionDigits(maximumFractionDigits);

  return numberFormatter.format(amount);
}
